// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { InvoicesService } from "src/app/modules/balances/invoices.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Component({
  selector: "app-edit-invoice-dialog",
  templateUrl: "./edit-invoice-dialog.component.html",
  styleUrls: ["./edit-invoice-dialog.component.scss"],
})
export class EditInvoiceDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  availableBalances = [];
  searchAll = true;
  previousValue: any = {};
  previousTotValue: any = {};

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add subUser form

  dialogIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditInvoiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private invoicesService: InvoicesService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    if (this.data.availableOptions) {
      this.availableBalances = this.data.availableOptions;
    }

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle click on Denial button
   *
   * @since 1.0.0
   */
  onDenial(): void {
    this.dialogRef.close();
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      // Set errors inside invalid form controls
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.controls[key];
        if (control.invalid) {
          control.markAsTouched();
          control.setErrors({
            required: true,
          });
        }
      });
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.form.value) {
      this.subscriptions.push(
        this.invoicesService
          .updateFields(this.data.invoiceId, this.form.value)
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                this.dialogRef.close();
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      code: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      vat: null,
      totalRow: 0,
    };

    this.fields = [];

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-sm-6",
            type: "input",
            key: "number",
            defaultValue: this.data.number,
            templateOptions: {
              label: this.translateService.instant("invoices.fields.number"),
              placeholder: this.translateService.instant(
                "invoices.fields.number"
              ),
              required: true,
            },
          },
          {
            className: "col-12 col-sm-6",
            type: "datepicker",
            key: "date",
            defaultValue: this.data.date,
            templateOptions: {
              label: this.translateService.instant("invoices.fields.date"),
              placeholder: this.translateService.instant(
                "invoices.fields.date"
              ),
              required: true,
            },
          },
        ],
      },
    ];
  }
}
